// @givmo/compliance — the filing panel's in-place forms (CCM-R1-INTAKE (a); frame 18b; the copy
// ruling of 2026-09-30 18:00 PDT, items 3, 7 and 10).
//
// ⚠ THE RECEIPT LAW HOLDS HERE AS EVERYWHERE: "Confirmed" saves only with the confirmation document
// attached — the button stays disabled and nothing is sent without it. "Sent, not confirmed yet" is
// never a filing; it is recorded as sent until the confirmation comes.
//
// ⚠ EACH YEAR IS WRITTEN THROUGH ITS OWN DOOR (design go D1): a library filing's year through the
// cycle's (with the cycle's revision as `expected_revision`), a one-time filing through the
// obligation's, an own filing's period through its own. Which kind of receipt a confirmed library
// filing records is the mechanical map (design go D3), never a question.

import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Field,
  Icon,
  IconButton,
  RadioTile,
  RadioTileGroup,
  Select,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { FileText, X } from 'lucide-react';
import { ArtifactUpload } from './artifact-upload';
import { useCompliance } from './compliance-context';
import { ComplianceDateField } from './compliance-date-field';
import {
  displayArtifactSize,
  isYearEndChangeUnanswered,
  serverMessage,
  type ComplianceArtifact,
  type ComplianceObligation,
  type RegistrationImport,
  type YearEndChange,
} from './compliance-decode';
import type { OwnFilingCadence, OwnFilingCycle, RenewalCycle } from './compliance-filings-decode';
import { ANCHOR_LABELS, DOCUMENT_ROLE_NAMES, type AnchorDateKind } from './compliance-panel-model';

const PREFIX = 'compliance-panel';

/** Every Compliance read: a recorded act moves the list, the years, the calendar and the documents. */
function useRefreshCompliance() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['compliance'] });
}

export function FormCard({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <div
      className="flex flex-col gap-14 rounded-lg border border-border-subtle p-16"
      {...testIdProp(testId)}
    >
      {children}
    </div>
  );
}

function SaveCancel({
  save = 'Save',
  disabled,
  pending,
  onCancel,
}: {
  save?: string;
  disabled: boolean;
  pending: boolean;
  onCancel?: () => void;
}) {
  return (
    <div className="flex gap-8">
      <Button
        appearance="saas"
        tone="primary"
        size="sm"
        type="submit"
        disabled={disabled || pending}
        loading={pending}
      >
        {save}
      </Button>
      {onCancel !== undefined && (
        <Button appearance="saas" tone="ghost" size="sm" type="button" onClick={onCancel}>
          Cancel
        </Button>
      )}
    </div>
  );
}

// ── Attaching a document ───────────────────────────────────────────────────────────────────────

/** One document slot: the attach button, then — once attached — its name and size under its role. */
export function AttachSlot({
  button,
  label,
  artifact,
  onChange,
  testId,
  hint,
}: {
  button: string;
  label: string;
  artifact: ComplianceArtifact | undefined;
  onChange: (artifact: ComplianceArtifact | undefined) => void;
  testId: string;
  /** A quiet line under the button while nothing is attached (c3's own-account helper). */
  hint?: string;
}) {
  if (artifact === undefined) {
    return (
      <ArtifactUpload
        label={button}
        testId={testId}
        onUploaded={(next) => onChange(next)}
        {...(hint !== undefined
          ? {
              hint: (
                <p
                  className="text-s12 leading-h15 text-text-tertiary"
                  {...testIdProp(`${testId}-hint`)}
                >
                  {hint}
                </p>
              ),
            }
          : {})}
      />
    );
  }
  return (
    <div className="flex w-full flex-col gap-6" {...testIdProp(`${testId}-attached`)}>
      <span className="text-s13 font-w500 text-text-secondary">{label}</span>
      <div className="flex items-center gap-10 rounded-md border border-border-subtle px-12 py-10">
        <Icon icon={FileText} size={16} className="shrink-0 text-icon-muted" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-s14 text-text">{artifact.fileName}</span>
        {artifact.byteSize !== undefined && (
          <span className="shrink-0 text-s12 text-text-tertiary">
            {displayArtifactSize(artifact.byteSize)}
          </span>
        )}
        <IconButton
          size="sm"
          tone="ghost"
          aria-label={`Remove ${artifact.fileName}`}
          onClick={() => onChange(undefined)}
        >
          <Icon icon={X} size={14} aria-hidden />
        </IconButton>
      </div>
    </div>
  );
}

// ── Record this filing (frame 18b) ──────────────────────────────────────────────────────────────

/** Where a record goes: one library year, a one-time filing's obligation, or an own period. */
export type RecordTarget =
  | {
      kind: 'renewal-cycle';
      cycle: RenewalCycle;
      receiptKind: 'registered_filed' | 'exempt_filed' | 'renewed_filed';
    }
  | {
      kind: 'obligation';
      obligation: ComplianceObligation;
      receiptKind: 'registered_filed' | 'exempt_filed' | 'renewed_filed';
    }
  | { kind: 'own-cycle'; filingId: string; cycle: OwnFilingCycle };

export interface RecordFilingFormProps {
  target: RecordTarget;
  /** "For the fiscal year ended Jun 30, 2026", where the period is a fiscal year. */
  periodLine?: string;
  /** Who answers with the confirmation ("Maryland"); unnamed for an own filing with no agency. */
  answeredBy?: string;
  /** "Attach the confirmation" opens it on Confirmed for a year already recorded as sent. */
  initial?: 'confirmed' | 'sent';
  onDone: () => void;
}

export function RecordFilingForm({
  target,
  periodLine,
  answeredBy,
  initial = 'confirmed',
  onDone,
}: RecordFilingFormProps) {
  const { orgRef, operations } = useCompliance();
  const refresh = useRefreshCompliance();
  const [status, setStatus] = useState<'confirmed' | 'sent'>(initial);
  const [date, setDate] = useState('');
  const [confirmation, setConfirmation] = useState<ComplianceArtifact | undefined>();
  const [filedCopy, setFiledCopy] = useState<ComplianceArtifact | undefined>();
  const [proof, setProof] = useState<ComplianceArtifact | undefined>();
  const [attachment, setAttachment] = useState<ComplianceArtifact | undefined>();
  // An own filing's period takes no other document yet (CCM-R1-FILING-CYCLES-OWN, not landed).
  const takesAttachments = target.kind !== 'own-cycle';
  // A period already recorded as sent waits only for its confirmation: no choice to make again,
  // and no second "filing as sent" where it already carries one.
  const alreadySent =
    target.kind === 'obligation'
      ? target.obligation.filingRecordState === 'submission_recorded'
      : target.cycle.submissionRecordedOn !== undefined;
  const carriesFiledCopy =
    target.kind === 'renewal-cycle'
      ? target.cycle.documents.some((document) => document.role === 'filed_copy')
      : target.kind === 'own-cycle' && target.cycle.filedCopyArtifactId !== undefined;

  const record = useMutation({
    mutationFn: async () => {
      const ref = orgRef ?? '';
      if (status === 'confirmed') {
        if (confirmation === undefined) throw new Error('A confirmation is required.');
        if (target.kind === 'renewal-cycle') {
          await operations.recordRenewalFiling(ref, target.cycle.cycleId, {
            filing_record_state: target.receiptKind,
            confirmation_artifact_id: confirmation.artifactId,
            recorded_filed_on: date,
            expected_revision: target.cycle.revision,
            ...(filedCopy !== undefined ? { filed_copy_artifact_id: filedCopy.artifactId } : {}),
          });
          if (attachment !== undefined) {
            await operations.addRenewalAttachment(ref, target.cycle.cycleId, {
              artifact_id: attachment.artifactId,
            });
          }
        } else if (target.kind === 'obligation') {
          const obligationId = target.obligation.obligationId;
          await operations.recordFiling(ref, obligationId, {
            filing_record_state: target.receiptKind,
            confirmation_artifact_id: confirmation.artifactId,
            recorded_filed_on: date,
          });
          if (filedCopy !== undefined) {
            await operations.attachObligationFiledCopy(ref, obligationId, {
              artifact_id: filedCopy.artifactId,
            });
          }
          if (attachment !== undefined) {
            await operations.addObligationAttachment(ref, obligationId, {
              artifact_id: attachment.artifactId,
            });
          }
        } else {
          await operations.recordOrganizationFilingFiled(
            ref,
            target.filingId,
            target.cycle.cycleId,
            {
              confirmation_artifact_id: confirmation.artifactId,
              recorded_filed_on: date,
              ...(filedCopy !== undefined ? { filed_copy_artifact_id: filedCopy.artifactId } : {}),
            },
          );
        }
        return 'filed' as const;
      }
      if (target.kind === 'renewal-cycle') {
        await operations.recordRenewalSubmission(ref, target.cycle.cycleId, {
          submission_recorded_on: date,
          expected_revision: target.cycle.revision,
          ...(proof !== undefined ? { submission_proof_artifact_id: proof.artifactId } : {}),
        });
        if (filedCopy !== undefined) {
          await operations.attachRenewalFiledCopy(ref, target.cycle.cycleId, {
            artifact_id: filedCopy.artifactId,
          });
        }
      } else if (target.kind === 'obligation') {
        const obligationId = target.obligation.obligationId;
        await operations.recordSubmission(ref, obligationId, {
          submission_recorded_on: date,
          ...(proof !== undefined ? { submission_proof_artifact_id: proof.artifactId } : {}),
        });
        if (filedCopy !== undefined) {
          await operations.attachObligationFiledCopy(ref, obligationId, {
            artifact_id: filedCopy.artifactId,
          });
        }
      } else {
        await operations.recordOrganizationFilingSent(ref, target.filingId, target.cycle.cycleId, {
          submission_recorded_on: date,
          ...(filedCopy !== undefined ? { filed_copy_artifact_id: filedCopy.artifactId } : {}),
          ...(proof !== undefined ? { submission_proof_artifact_id: proof.artifactId } : {}),
        });
      }
      return 'sent' as const;
    },
    onSuccess: (recorded) => {
      toast.success(recorded === 'filed' ? 'Filing recorded.' : 'Recorded as sent.');
      void refresh();
      onDone();
    },
    onError: (error) =>
      toast.error(serverMessage(error, 'We couldn’t record that filing. Please try again.')),
  });

  // The receipt law: no confirmation document, no "Confirmed" save.
  const ready = date !== '' && (status === 'sent' || confirmation !== undefined);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) record.mutate();
  };

  return (
    <form onSubmit={submit}>
      <FormCard testId={`${PREFIX}-record`}>
        <div className="flex flex-col gap-2">
          <p className="text-s14 font-w600 text-text-strong">Record this filing</p>
          {periodLine !== undefined && <p className="text-s12 text-text-tertiary">{periodLine}</p>}
        </div>
        {!alreadySent && (
          <RadioTileGroup
            value={status}
            onValueChange={(next) => setStatus(next === 'sent' ? 'sent' : 'confirmed')}
            aria-label="Your last filing"
            testId={`${PREFIX}-record-status`}
          >
            <RadioTile
              value="confirmed"
              label="Confirmed"
              {...(answeredBy !== undefined
                ? { description: `${answeredBy} sent back a confirmation, letter or license.` }
                : {})}
            />
            <RadioTile
              value="sent"
              label="Sent, not confirmed yet"
              description="It’s recorded as sent. It isn’t marked filed until the confirmation is attached."
            />
          </RadioTileGroup>
        )}
        <ComplianceDateField
          label={status === 'confirmed' ? 'Date filed' : 'Date sent'}
          value={date}
          onChange={setDate}
          testId={`${PREFIX}-record-date`}
        />
        <div className="flex flex-col items-start gap-8">
          {status === 'confirmed' ? (
            <>
              <AttachSlot
                button="Attach the confirmation"
                label={DOCUMENT_ROLE_NAMES.confirmation}
                artifact={confirmation}
                onChange={setConfirmation}
                testId={`${PREFIX}-record-confirmation`}
              />
              {!carriesFiledCopy && (
                <AttachSlot
                  button="Attach the filing as sent (optional)"
                  label={DOCUMENT_ROLE_NAMES.filed_copy}
                  artifact={filedCopy}
                  onChange={setFiledCopy}
                  testId={`${PREFIX}-record-filed-copy`}
                />
              )}
              {takesAttachments && (
                <AttachSlot
                  button="Attach another document (optional)"
                  label={DOCUMENT_ROLE_NAMES.attachment}
                  artifact={attachment}
                  onChange={setAttachment}
                  testId={`${PREFIX}-record-attachment`}
                />
              )}
            </>
          ) : (
            <>
              <AttachSlot
                button="Attach the filing as sent (optional)"
                label={DOCUMENT_ROLE_NAMES.filed_copy}
                artifact={filedCopy}
                onChange={setFiledCopy}
                testId={`${PREFIX}-record-filed-copy`}
              />
              <AttachSlot
                button="Attach proof you sent it (optional)"
                label={DOCUMENT_ROLE_NAMES.submission_proof}
                artifact={proof}
                onChange={setProof}
                testId={`${PREFIX}-record-proof`}
              />
            </>
          )}
        </div>
        <SaveCancel disabled={!ready} pending={record.isPending} onCancel={onDone} />
      </FormCard>
    </form>
  );
}

// ── The number: Correct or Add (copy ruling item 7) ─────────────────────────────────────────────

export type NumberTarget =
  | { kind: 'import'; registrationImport: RegistrationImport }
  | { kind: 'obligation'; obligation: ComplianceObligation }
  | { kind: 'own'; filingId: string };

export function NumberForm({
  target,
  label,
  current,
  onDone,
}: {
  target: NumberTarget;
  /** The number's own label: "Registration number", "License number", "Number". */
  label: string;
  /** The number on record, when correcting it. */
  current?: string;
  onDone: () => void;
}) {
  const { orgRef, operations } = useCompliance();
  const refresh = useRefreshCompliance();
  const correcting = current !== undefined;
  const [value, setValue] = useState(current ?? '');
  const [reason, setReason] = useState('');
  const trimmed = value.trim();
  const save = useMutation({
    mutationFn: async () => {
      const ref = orgRef ?? '';
      if (target.kind === 'import') {
        // A correction is recorded BESIDE the original, which stays visible (CCM-BE6).
        await operations.amendRegistrationImport(ref, target.registrationImport.importId, {
          field_key: 'recorded_registration_number',
          amended_value: trimmed,
          ...(reason.trim().length > 0 ? { reason: reason.trim() } : {}),
        });
      } else if (target.kind === 'obligation') {
        // A confirmed filing with no import row: the import writes the row with the number, and
        // asks for nothing it does not have.
        await operations.importRegistration(ref, {
          jurisdiction_code: target.obligation.jurisdictionCode,
          rule_id: target.obligation.ruleId,
          recorded_registration_number: trimmed,
        });
      } else {
        // The own filing's history keeps the change and these words beside the number it replaced
        // (CCM-R1-OWN-FILINGS-HISTORY), as the import's amendment does for a library filing.
        await operations.updateOrganizationFiling(ref, target.filingId, {
          number: trimmed,
          ...(correcting && reason.trim().length > 0 ? { reason: reason.trim() } : {}),
        });
      }
    },
    onSuccess: () => {
      void refresh();
      onDone();
    },
    onError: (error) =>
      toast.error(serverMessage(error, 'We couldn’t save that number. Please try again.')),
  });
  const ready = trimmed.length > 0 && trimmed !== current;
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <FormCard testId={`${PREFIX}-number-form`}>
        {correcting && <p className="text-s14 font-w600 text-text-strong">Correct the number</p>}
        <Field
          label={label}
          value={value}
          autoFocus
          containerClassName="max-w-[320px]"
          onChange={(event) => setValue(event.target.value)}
        />
        {correcting && (target.kind === 'import' || target.kind === 'own') && (
          <Field
            label="Why (optional)"
            value={reason}
            maxLength={2000}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
        <SaveCancel disabled={!ready} pending={save.isPending} onCancel={onDone} />
      </FormCard>
    </form>
  );
}

// ── Change the fiscal year end (copy ruling item 7: the add sheet's control in place) ───────────
//
// Each of the three year-end questions (the panel's, the workbench's item, the add sheet's) groups
// its two selects under its own "Your fiscal year ends" (`role="group"`, CCM-R1-INTAKE-FYE-A11Y), so
// "Month" and "Day" are heard as the parts of one answer; and none drops focus to the page when its
// buttons go after a save: focus moves to what the save changed, or back into the question.

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * A chosen day kept inside a newly chosen month: one the month doesn't have moves to the month's
 * last (December 31 → June 30), never to a date that doesn't exist; a day the month has stays.
 */
function dayWithinMonth(month: string, day: string): string {
  const last = month === '' ? 31 : (DAYS_IN_MONTH[Number(month) - 1] ?? 31);
  return day !== '' && Number(day) > last ? String(last) : day;
}

/**
 * "June 30", or the recorded "06-30" (the backend's `month_day`, `MM-DD`) → { month: 6, day: 30 };
 * anything else reads as nothing.
 */
export function parseMonthDay(
  text: string | undefined,
): { month: number; day: number } | undefined {
  const trimmed = text?.trim() ?? '';
  const recorded = /^(\d{2})-(\d{2})$/.exec(trimmed);
  const worded = /^([A-Za-z]+)\s+(\d{1,2})$/.exec(trimmed);
  const month =
    recorded !== null
      ? Number(recorded[1]) - 1
      : worded !== null
        ? MONTHS.findIndex((name) => name.toLowerCase() === (worded[1] ?? '').toLowerCase())
        : -1;
  const day = Number((recorded ?? worded)?.[2]);
  if (month < 0 || month > 11 || day < 1 || day > (DAYS_IN_MONTH[month] ?? 0)) return undefined;
  return { month: month + 1, day };
}

// ── CCM-BE24-B (D-CCM-74, REVIEW-23): was the earlier year end wrong, or did it change? ─────────

/** "December 31": a year end as the question names it, never split across lines. */
function monthDayWords({ month, day }: { month: number; day: number }): string {
  return `${MONTHS[month - 1] ?? ''}\u00a0${day}`;
}

/** Y1. */
export function yearEndQuestion(earlier: string, organization: string): string {
  return `Was ${earlier} wrong, or did ${organization}’s fiscal year change?`;
}
/** Y2, the first answer. */
export function yearEndWasWrong(earlier: string, chosen: string): string {
  return `${earlier} was wrong. It has always ended ${chosen}.`;
}
/** Y4, under the first answer. */
export function yearEndWasWrongMeans(chosen: string): string {
  return `Givmo moves the years with nothing recorded to ${chosen}. Years with a filing recorded stay as they are.`;
}
/** Y3, the second answer. */
export function yearEndChanged(earlier: string): string {
  return `It changed. Earlier years ended ${earlier}.`;
}
/** Y5, under the second answer. */
export function yearEndChangedMeans(start: string, end: string): string {
  return `Givmo keeps the earlier years and adds the short year from ${start} to ${end}.`;
}

/**
 * The short year's first day under "It changed": the day after the earlier year end's last one on
 * or before `today` (`YYYY-MM-DD`), as the backend dates it ("January 1" after December 31, 2025).
 */
export function shortYearStart(
  earlier: { month: number; day: number },
  today: string,
): { month: number; day: number } {
  const [year, month, day] = today.split('-').map(Number);
  const on = (y: number) =>
    Date.UTC(
      y,
      earlier.month - 1,
      Math.min(earlier.day, new Date(Date.UTC(y, earlier.month, 0)).getUTCDate()),
    );
  const todayAt = Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1);
  const thisYear = on(year ?? 1970);
  const last = thisYear <= todayAt ? thisYear : on((year ?? 1970) - 1);
  const next = new Date(last + 24 * 60 * 60 * 1000);
  return { month: next.getUTCMonth() + 1, day: next.getUTCDate() };
}

/**
 * A fiscal year end that didn't save. Refused for want of the organization's answer (Y7, the API's and
 * the connected AI's words only), the reads are fetched again: the profile then says the move asks, and
 * the form asks Y1 with Save waiting. Anything else says it couldn't save (cut 1008-42's review, F4).
 */
export function yearEndSaveFailed(error: unknown, refresh: () => Promise<unknown>): void {
  if (isYearEndChangeUnanswered(error)) {
    void refresh();
    return;
  }
  toast.error(serverMessage(error, 'We couldn’t save your fiscal year end. Please try again.'));
}

/**
 * Y1–Y5 (REVIEW-23's form): once a new year end is chosen, where a confirmed filing's years count
 * from it (the profile's `yearEndChangeRequired`), the organization says whether the earlier one was
 * wrong or the fiscal year changed. Save waits for the answer, which goes with the year end.
 */
export function YearEndChangeQuestion({
  earlier,
  chosen,
  organization,
  today,
  value,
  onChange,
  disabled,
  testId,
}: {
  earlier: { month: number; day: number };
  chosen: { month: number; day: number };
  organization: string;
  today: string;
  value: YearEndChange | undefined;
  onChange: (value: YearEndChange) => void;
  disabled?: boolean;
  testId: string;
}) {
  const before = monthDayWords(earlier);
  const after = monthDayWords(chosen);
  const question = yearEndQuestion(before, organization);
  return (
    <div className="flex flex-col gap-8" {...testIdProp(testId)}>
      <span className="text-s14 font-w600 text-text-strong">{question}</span>
      <RadioTileGroup
        value={value ?? ''}
        onValueChange={(next) => onChange(next === 'change' ? 'change' : 'correction')}
        aria-label={question}
        disabled={disabled}
        testId={`${testId}-choice`}
      >
        <RadioTile
          value="correction"
          label={yearEndWasWrong(before, after)}
          description={yearEndWasWrongMeans(after)}
          testId={`${testId}-correction`}
        />
        <RadioTile
          value="change"
          label={yearEndChanged(before)}
          description={yearEndChangedMeans(monthDayWords(shortYearStart(earlier, today)), after)}
          testId={`${testId}-change`}
        />
      </RadioTileGroup>
    </div>
  );
}

export function FiscalYearEndForm({
  current,
  onSaved,
  onCancel,
  asks,
}: {
  /** The fiscal year end on record, as the profile shows it ("June 30"). */
  current?: string;
  /** Saved, once Compliance's reads hold the new year end: the panel then puts focus on the date. */
  onSaved: () => void;
  /**
   * A Change's Cancel. The question asked while no year end is on record has none: it stays a
   * question until it is answered, so a Cancel there would do nothing (as the workbench's item 2).
   */
  onCancel?: () => void;
  /**
   * CCM-BE24-B: where a confirmed filing's years count from the year end (the profile's
   * `yearEndChangeRequired`), the organization's name and today: a moved year end then asks Y1.
   */
  asks?: { organization: string; today: string };
}) {
  const { orgRef, operations } = useCompliance();
  const refresh = useRefreshCompliance();
  const labelId = useId();
  const recorded = parseMonthDay(current);
  const [month, setMonth] = useState(recorded !== undefined ? String(recorded.month) : '');
  const [day, setDay] = useState(recorded !== undefined ? String(recorded.day) : '');
  const [change, setChange] = useState<YearEndChange | undefined>();
  const days = month === '' ? 31 : (DAYS_IN_MONTH[Number(month) - 1] ?? 31);
  const moved =
    recorded !== undefined &&
    month !== '' &&
    day !== '' &&
    (recorded.month !== Number(month) || recorded.day !== Number(day));
  const asking = moved && asks !== undefined ? asks : undefined;
  const save = useMutation({
    mutationFn: () =>
      operations.recordProfileParameter(orgRef ?? '', {
        parameter_key: 'fiscal_year_end',
        value: {
          fiscal_year_end_month: Number(month),
          fiscal_year_end_day: Number(day),
          ...(asking !== undefined && change !== undefined ? { year_end_change: change } : {}),
        },
      }),
    // Closed only once the reads hold the new year end, so focus lands on the date it moved.
    onSuccess: async () => {
      await refresh();
      onSaved();
    },
    onError: (error) => yearEndSaveFailed(error, refresh),
  });
  const ready =
    month !== '' &&
    day !== '' &&
    Number(day) <= days &&
    (recorded === undefined || recorded.month !== Number(month) || recorded.day !== Number(day)) &&
    (asking === undefined || change !== undefined);
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <FormCard testId={`${PREFIX}-fiscal-year-end`}>
        <div className="flex flex-col gap-6">
          <span id={labelId} className="text-s13 font-w500 text-text-secondary">
            Your fiscal year ends
          </span>
          <div role="group" aria-labelledby={labelId} className="flex gap-8">
            <Select
              appearance="saas"
              aria-label="Month"
              className="w-[200px]"
              value={month === '' ? null : month}
              onValueChange={(next) => {
                const chosen = next ?? '';
                setMonth(chosen);
                setDay((current) => dayWithinMonth(chosen, current));
              }}
              options={MONTHS.map((name, index) => ({ value: String(index + 1), label: name }))}
            />
            {/* As wide as the design system's own "Select…", shown while nothing is recorded. */}
            <Select
              appearance="saas"
              aria-label="Day"
              className="w-[108px]"
              value={day === '' ? null : day}
              onValueChange={(next) => setDay(next ?? '')}
              options={Array.from({ length: days }, (_, index) => ({
                value: String(index + 1),
                label: String(index + 1),
              }))}
            />
          </div>
          <span className="text-s12 text-text-tertiary">
            Saved for your organization, not just this filing.
          </span>
        </div>
        {asking !== undefined && recorded !== undefined && (
          <YearEndChangeQuestion
            earlier={recorded}
            chosen={{ month: Number(month), day: Number(day) }}
            organization={asking.organization}
            today={asking.today}
            value={change}
            onChange={setChange}
            disabled={save.isPending}
            testId={`${PREFIX}-year-end-change`}
          />
        )}
        <SaveCancel disabled={!ready} pending={save.isPending} onCancel={onCancel} />
      </FormCard>
    </form>
  );
}

/**
 * The workbench's own question for the organization's fiscal year end (frame 34's item 2;
 * CCM-R1-INTAKE-NODATE-FYE): asked in place, as the form's other items are — "Your fiscal year
 * ends", the month and the day under the frame's own "Month" and "Day", Save in their row, and the
 * built "Saved for your organization, not just this filing." No card and no Cancel: the item is never
 * in edit mode, so a Cancel would have nothing to go back to. It saves to the profile (one fact, as
 * the panel's form does), which then fills the item from the record.
 */
export function WorkbenchFiscalYearEnd({
  current,
  testId,
  onSaved,
}: {
  /** The fiscal year end on record, as the profile shows it ("June 30"), or nothing. */
  current?: string;
  testId: string;
  /** Saved, once the reads hold it: the item, filled from the record, takes focus. */
  onSaved?: () => void;
}) {
  const { orgRef, operations } = useCompliance();
  const refresh = useRefreshCompliance();
  const labelId = useId();
  const recorded = parseMonthDay(current);
  const [month, setMonth] = useState(recorded !== undefined ? String(recorded.month) : '');
  const [day, setDay] = useState(recorded !== undefined ? String(recorded.day) : '');
  const days = month === '' ? 31 : (DAYS_IN_MONTH[Number(month) - 1] ?? 31);
  const save = useMutation({
    mutationFn: () =>
      operations.recordProfileParameter(orgRef ?? '', {
        parameter_key: 'fiscal_year_end',
        value: { fiscal_year_end_month: Number(month), fiscal_year_end_day: Number(day) },
      }),
    onSuccess: async () => {
      await refresh();
      onSaved?.();
    },
    onError: (error) => yearEndSaveFailed(error, refresh),
  });
  const ready =
    month !== '' &&
    day !== '' &&
    Number(day) <= days &&
    (recorded === undefined || recorded.month !== Number(month) || recorded.day !== Number(day));
  return (
    <div className="flex flex-col gap-6" {...testIdProp(testId)}>
      <span id={labelId} className="text-s13 font-w500 text-text-secondary">
        Your fiscal year ends
      </span>
      <div className="flex flex-wrap items-center gap-8">
        <div role="group" aria-labelledby={labelId} className="flex flex-wrap items-center gap-8">
          <Select
            appearance="saas"
            aria-label="Month"
            placeholder="Month"
            className="w-[200px]"
            value={month === '' ? null : month}
            onValueChange={(next) => {
              const chosen = next ?? '';
              setMonth(chosen);
              setDay((current) => dayWithinMonth(chosen, current));
            }}
            options={MONTHS.map((name, index) => ({ value: String(index + 1), label: name }))}
            testId={`${testId}-month`}
          />
          <Select
            appearance="saas"
            aria-label="Day"
            placeholder="Day"
            className="w-[88px]"
            value={day === '' ? null : day}
            onValueChange={(next) => setDay(next ?? '')}
            options={Array.from({ length: days }, (_, index) => ({
              value: String(index + 1),
              label: String(index + 1),
            }))}
            testId={`${testId}-day`}
          />
        </div>
        <Button
          appearance="saas"
          tone="primary"
          size="sm"
          disabled={!ready}
          loading={save.isPending}
          onClick={() => save.mutate()}
          testId={`${testId}-save`}
        >
          Save
        </Button>
      </div>
      <span className="text-s12 text-text-tertiary">
        Saved for your organization, not just this filing.
      </span>
    </div>
  );
}

/**
 * The add sheet's fiscal year end question (frame 03; CCM-R1-INTAKE-NEXTDUE): the panel's control in
 * the sheet's layout, prefilled with what is recorded. It saves only through Save, which shows once
 * the choice differs from the record (the orchestrator's Q2, 2026-10-01): a select alone never writes
 * an organization-wide fact that the sheet's Cancel could not undo. Its buttons never submit the
 * sheet's own form.
 */
export function SheetFiscalYearEnd({
  recorded,
  onUnsavedChange,
  asks,
}: {
  /** The fiscal year end on record, as the profile shows it ("June 30"), or nothing. */
  recorded?: string;
  /** Told whenever the choice starts or stops differing from the record. */
  onUnsavedChange: (unsaved: boolean) => void;
  /**
   * CCM-BE24-B (cut 1008-42's review, F4): where a confirmed filing's years count from the year end
   * (the profile's `yearEndChangeRequired`), the organization's name and today: a moved year end then
   * asks Y1 under the field, and Save waits, as the panel's form does.
   */
  asks?: { organization: string; today: string };
}) {
  const { orgRef, operations } = useCompliance();
  const refresh = useRefreshCompliance();
  const labelId = useId();
  const cancelId = useId();
  const group = useRef<HTMLDivElement>(null);
  // Save, or this question's Cancel, takes its two buttons away: focus goes back to the Month.
  const refocus = useRef(false);
  const onRecord = parseMonthDay(recorded);
  const recordedMonth = onRecord !== undefined ? String(onRecord.month) : '';
  const recordedDay = onRecord !== undefined ? String(onRecord.day) : '';
  const [month, setMonth] = useState(recordedMonth);
  const [day, setDay] = useState(recordedDay);
  const [change, setChange] = useState<YearEndChange | undefined>();
  // The record moved underneath (this sheet's save, or another screen's): the question follows it.
  useEffect(() => {
    setMonth(recordedMonth);
    setDay(recordedDay);
    setChange(undefined);
  }, [recordedMonth, recordedDay]);
  const days = month === '' ? 31 : (DAYS_IN_MONTH[Number(month) - 1] ?? 31);
  const unsaved = month !== recordedMonth || day !== recordedDay;
  // CCM-BE24-B: a recorded year end moved where a confirmed filing counts from it asks Y1.
  const asking =
    asks !== undefined && onRecord !== undefined && unsaved && month !== '' && day !== ''
      ? asks
      : undefined;
  useEffect(() => {
    onUnsavedChange(unsaved);
  }, [unsaved, onUnsavedChange]);
  useEffect(() => {
    if (unsaved || !refocus.current) return;
    refocus.current = false;
    group.current?.querySelector<HTMLElement>('[role="combobox"]')?.focus();
  }, [unsaved]);
  const save = useMutation({
    mutationFn: () =>
      operations.recordProfileParameter(orgRef ?? '', {
        parameter_key: 'fiscal_year_end',
        value: {
          fiscal_year_end_month: Number(month),
          fiscal_year_end_day: Number(day),
          ...(asking !== undefined && change !== undefined ? { year_end_change: change } : {}),
        },
      }),
    onSuccess: () => {
      refocus.current = true;
      void refresh();
    },
    onError: (error) => yearEndSaveFailed(error, refresh),
  });
  const ready =
    month !== '' &&
    day !== '' &&
    Number(day) <= days &&
    (asking === undefined || change !== undefined);
  return (
    <div className="flex flex-col gap-6" {...testIdProp(`${PREFIX}-sheet-fiscal-year-end`)}>
      <span id={labelId} className="text-s13 font-w500 text-text-secondary">
        Your fiscal year ends
      </span>
      {/* The day's column fits the Select's own "Select…", shown while nothing is recorded. */}
      <div
        ref={group}
        role="group"
        aria-labelledby={labelId}
        className="grid max-w-[320px] grid-cols-[minmax(0,1fr)_108px] gap-8"
      >
        <Select
          appearance="saas"
          aria-label="Month"
          value={month === '' ? null : month}
          onValueChange={(next) => {
            const chosen = next ?? '';
            setMonth(chosen);
            setDay((current) => dayWithinMonth(chosen, current));
          }}
          options={MONTHS.map((name, index) => ({ value: String(index + 1), label: name }))}
        />
        <Select
          appearance="saas"
          aria-label="Day"
          value={day === '' ? null : day}
          onValueChange={(next) => setDay(next ?? '')}
          options={Array.from({ length: days }, (_, index) => ({
            value: String(index + 1),
            label: String(index + 1),
          }))}
        />
      </div>
      <span className="text-s12 text-text-tertiary">
        Saved for your organization, not just this filing.
      </span>
      {asking !== undefined && onRecord !== undefined && (
        <div className="pt-4">
          <YearEndChangeQuestion
            earlier={onRecord}
            chosen={{ month: Number(month), day: Number(day) }}
            organization={asking.organization}
            today={asking.today}
            value={change}
            onChange={setChange}
            disabled={save.isPending}
            testId={`${PREFIX}-sheet-year-end-change`}
          />
        </div>
      )}
      {unsaved && (
        <div className="flex gap-8 pt-4">
          <Button
            appearance="saas"
            tone="primary"
            size="sm"
            type="button"
            disabled={!ready || save.isPending}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
          {/* Not the sheet's own Cancel: named with the question it puts back, so the two are
              told apart by what they do ("Cancel Your fiscal year ends"). */}
          <Button
            id={cancelId}
            aria-labelledby={`${cancelId} ${labelId}`}
            appearance="saas"
            tone="secondary"
            size="sm"
            type="button"
            onClick={() => {
              refocus.current = true;
              setMonth(recordedMonth);
              setDay(recordedDay);
              setChange(undefined);
            }}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

// ── A date only the organization has: a licence's expiry, a registration date, its own date ─────

export type { AnchorDateKind };

/**
 * The date in place: the question under "Not worked out yet" (no Cancel; it stays a question until
 * answered), or a "Change" (with Cancel).
 */
export function AnchorDateForm({
  kind,
  obligationId,
  filingId,
  ownCadence,
  initial,
  question,
  note,
  footer,
  onDone,
}: {
  kind: AnchorDateKind;
  obligationId?: string;
  filingId?: string;
  /** An own filing's cadence: a yearly one moves its date each year, not only the next one. */
  ownCadence?: OwnFilingCadence;
  /** The date on record, for a change (the field opens on it). */
  initial?: string;
  /** The question above the field ("Givmo needs one date from you …"), when it is one. */
  question?: string;
  /** A quiet line right under the field (frame 04's attached-license note). */
  note?: string;
  /** What sits under the field (the rule's source line, for the question). */
  footer?: ReactNode;
  onDone?: () => void;
}) {
  const { orgRef, operations } = useCompliance();
  const refresh = useRefreshCompliance();
  const [date, setDate] = useState(initial ?? '');
  const save = useMutation({
    mutationFn: async () => {
      const ref = orgRef ?? '';
      if (kind === 'own-date') {
        // A yearly filing's next date has to fall on its date each year (the backend refuses one
        // that does not), so changing it moves the yearly date itself: the cadence travels with
        // its month and day.
        const [, month, day] = date.split('-').map(Number);
        await operations.updateOrganizationFiling(
          ref,
          filingId ?? '',
          ownCadence === 'fixed_date_yearly'
            ? {
                cadence: 'fixed_date_yearly',
                cadence_month: month,
                cadence_day: day,
                next_due_on: date,
              }
            : { next_due_on: date },
        );
      } else {
        await operations.recordRegistrationDate(
          ref,
          obligationId ?? '',
          kind === 'license-expiry' ? { license_expires_on: date } : { registration_date: date },
        );
      }
    },
    onSuccess: () => {
      void refresh();
      onDone?.();
    },
    onError: (error) =>
      toast.error(serverMessage(error, 'We couldn’t save that date. Please try again.')),
  });
  const ready = date !== '' && date !== initial;
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <FormCard testId={`${PREFIX}-date-${kind}`}>
        {question !== undefined && <p className="text-s14 text-text">{question}</p>}
        <ComplianceDateField
          label={ANCHOR_LABELS[kind]}
          value={date}
          onChange={setDate}
          testId={`${PREFIX}-date-${kind}-field`}
        />
        {note !== undefined && (
          <p className="text-s12 text-text-tertiary" {...testIdProp(`${PREFIX}-date-${kind}-note`)}>
            {note}
          </p>
        )}
        {footer}
        <SaveCancel disabled={!ready} pending={save.isPending} onCancel={onDone} />
      </FormCard>
    </form>
  );
}

// ── Add an earlier year (copy ruling item 7) ────────────────────────────────────────────────────

export function EarlierYearForm({
  obligationId,
  deadlineIndex,
  years = [],
  filingId,
  onDone,
}: {
  /** A library filing's year-keyed shape: the years it may add (`earlierYearChoices`). */
  obligationId?: string;
  deadlineIndex?: number;
  years?: readonly number[];
  /** An own filing: a period is added by its date. */
  filingId?: string;
  onDone: () => void;
}) {
  const { orgRef, operations } = useCompliance();
  const refresh = useRefreshCompliance();
  const [year, setYear] = useState('');
  const [date, setDate] = useState('');
  const own = filingId !== undefined;
  const add = useMutation({
    mutationFn: async () => {
      const ref = orgRef ?? '';
      if (own) await operations.addOrganizationFilingCycle(ref, filingId, { due_on: date });
      else {
        await operations.addEarlierRenewalCycle(ref, obligationId ?? '', {
          cycle_year: Number(year),
          deadline_index: deadlineIndex ?? 0,
        });
      }
    },
    onSuccess: () => {
      void refresh();
      onDone();
    },
    onError: (error) =>
      toast.error(serverMessage(error, 'We couldn’t add that year. Please try again.')),
  });
  const ready = own ? date !== '' : year !== '';
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) add.mutate();
      }}
    >
      <FormCard testId={`${PREFIX}-earlier-year`}>
        {own ? (
          <ComplianceDateField
            label="Its date"
            value={date}
            onChange={setDate}
            testId={`${PREFIX}-earlier-date`}
          />
        ) : (
          <Select
            appearance="saas"
            label="Which year?"
            className="w-[160px]"
            value={year === '' ? null : year}
            onValueChange={(next) => setYear(next ?? '')}
            options={years.map((candidate) => ({
              value: String(candidate),
              label: String(candidate),
            }))}
          />
        )}
        <SaveCancel save="Add" disabled={!ready} pending={add.isPending} onCancel={onDone} />
      </FormCard>
    </form>
  );
}
